// TeamView preferences persistence service using localStorage
// CREATED: Manage user-specific filter preferences for TeamView component
// Features: Save/restore site, service, profile, date selections, and selection mode (auto/manual)
// TTL: 7 days to avoid restoring stale preferences
// Format: tasq_teamview_prefs_{userId}
// CHANGE: Removed all debug console statements for production
// UPDATED: Added selectionMode field to track auto vs manual operator selection
// UPDATED: Added viewMode (calendar/list) and listPeriodType (week/month) fields

const CACHE_VERSION = '1.1';
const PREFERENCES_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export interface TeamViewPreferences {
  lastSiteId: string | null;
  lastServiceIds: string[];
  lastProfileIds: string[];
  lastDate: string | null;
  selectionMode: 'auto' | 'manual';
  viewMode: 'calendar' | 'list';
  listPeriodType: 'week' | 'month';
}

interface PreferencesCacheEntry {
  data: TeamViewPreferences;
  timestamp: number;
  version: string;
}

// Generate cache key for user preferences
function getPreferencesKey(userId: string): string {
  return `tasq_teamview_prefs_${userId}`;
}

// Check if cache entry is valid (not expired)
function isValidCache(entry: PreferencesCacheEntry | null): entry is PreferencesCacheEntry {
  if (!entry) return false;
  if (entry.version !== CACHE_VERSION) return false;

  const now = Date.now();
  const age = now - entry.timestamp;

  return age < PREFERENCES_TTL;
}

// Save TeamView preferences for a user
export function saveTeamViewPreferences(
  userId: string,
  preferences: TeamViewPreferences
): void {
  try {
    const key = getPreferencesKey(userId);
    const entry: PreferencesCacheEntry = {
      data: preferences,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };

    localStorage.setItem(key, JSON.stringify(entry));
  } catch (error) {
  }
}

// Get saved TeamView preferences for a user
export function getTeamViewPreferences(userId: string): TeamViewPreferences | null {
  try {
    const key = getPreferencesKey(userId);
    const cached = localStorage.getItem(key);

    if (!cached) return null;

    const entry: PreferencesCacheEntry = JSON.parse(cached);

    if (!isValidCache(entry)) {
      // Clean up expired preferences
      localStorage.removeItem(key);
      return null;
    }

    return entry.data;
  } catch (error) {
    return null;
  }
}

// Clear preferences for a user
export function clearTeamViewPreferences(userId: string): void {
  try {
    const key = getPreferencesKey(userId);
    localStorage.removeItem(key);
  } catch (error) {
  }
}

// Validate that saved preferences are still valid (referenced IDs still exist)
export function validatePreferences(
  preferences: TeamViewPreferences,
  availableSiteIds: string[],
  availableServiceIds: string[],
  availableProfileIds: string[]
): TeamViewPreferences {
  // Validate site ID
  const siteId = preferences.lastSiteId && availableSiteIds.includes(preferences.lastSiteId)
    ? preferences.lastSiteId
    : null;

  // Validate service IDs (filter out deleted/disabled services)
  const serviceIds = preferences.lastServiceIds.filter(id =>
    availableServiceIds.includes(id)
  );

  // Validate profile IDs (filter out removed users)
  const profileIds = preferences.lastProfileIds.filter(id =>
    availableProfileIds.includes(id)
  );

  return {
    lastSiteId: siteId,
    lastServiceIds: serviceIds,
    lastProfileIds: profileIds,
    lastDate: preferences.lastDate,
    selectionMode: preferences.selectionMode || 'auto',
    viewMode: preferences.viewMode || 'calendar',
    listPeriodType: preferences.listPeriodType || 'week',
  };
}
